import useOrbitChatHistory from '../../data/orbit/useOrbitChatHistory';
import { Alert } from '../base/Alert';
import { LoadingWheel } from '../base/LoadingWheel';
import { MiniTable } from '../base/MiniTable';
import date from 'date-and-time';

interface ChatHistoryItemProps {
  id: string;
  title: string;
  lastUpdated: string;
  isSelected: boolean;
  onClick: () => void;
}

function ChatHistoryItem({
  title,
  lastUpdated,
  isSelected,
  onClick,
}: ChatHistoryItemProps) {
  return (
    <div
      className={`flex flex-col px-4 py-3 hover:cursor-pointer ${isSelected ? 'bg-gray-400/30 dark:bg-white/30' : 'bg-gray-400/10 hover:bg-gray-400/20 dark:bg-white/10 dark:hover:bg-white/20'}`}
      onClick={onClick}
    >
      <span>{title}</span>
      <span className="text-gray-700 dark:text-gray-400">
        Last updated: {lastUpdated}
      </span>
    </div>
  );
}

interface ChatHistoryProps {
  selectedChatId?: string;
  onSelectChat: (id: string, title: string) => void;
}

export function ChatHistory({
  selectedChatId,
  onSelectChat,
}: ChatHistoryProps) {
  const { list } = useOrbitChatHistory();

  return (
    <div className="pb-4">
      {list.isRefetching && (
        <div className="flex items-center justify-start relative -mt-8 mb-2">
          <LoadingWheel size="xs" />
          <span className="ml-2 mt-0.5 text-xs  text-gray-500">
            Refreshing...
          </span>
        </div>
      )}
      <MiniTable className='overflow-y-auto max-h-[calc(100vh-486px)] min-h-72'>
        {list.isLoading ? (
          <div className="py-10 flex items-center justify-center">
            <LoadingWheel />
          </div>
        ) : list.isError || !list.data ? (
          <Alert
            severity="error"
            className="m-4"
            message="Failed to load chat history"
          />
        ) : list.data?.length === 0 ? (
          <Alert
            severity="info"
            className="m-4"
            message="No chat history found, start a new chat to see it here"
          />
        ) : (
          Array.from(list.data!)
            .reverse()
            .slice(0, 8)
            .map((chat) => (
              <ChatHistoryItem
                key={chat.id}
                id={chat.id}
                title={chat.title}
                lastUpdated={date.format(
                  new Date(chat.updatedOn!),
                  'DD/MM/YYYY HH:mm:ss'
                )}
                isSelected={chat.id === selectedChatId}
                onClick={() => onSelectChat(chat.id, chat.title)}
              />
            ))
        )}
        {list.data?.length! > 8 && (
          <div className="text-center text-sm text-gray-400 dark:text-gray-500 bg-gray-400/10 dark:bg-white/10 py-2">
            And {list.data?.length! - 8} more...
          </div>
        )}
      </MiniTable>
    </div>
  );
}
